import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Input, Switch } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  CtyShowToast,
  CtySpinner,
  ErrorBoundary,
  HorizontalRulesAgGrid,
  PageHeader,
  SimpleDeleteConfirmationModal,
  checkType,
} from '@circuitry-ai/doc-ui';
import {
  RootState,
  useGetWatchlistQuery,
  useUpdateWatchlistSubscriptionMutation,
} from '@circuitry-ai/doc-data';
import {
  formatWatchlistFrequency,
  isWatchlistNotFoundError,
  mapWatchlistRecordsToViews,
  type WatchlistAddedBy,
  type WatchlistSubscriptionView,
} from './watchlist/watchlist-utils';
import EmailSubscriptionBanner from './watchlist/email-subscription-banner';

const PageHeaderWithErrorBoundary = ErrorBoundary(PageHeader);

function formatAddedBy(addedBy: WatchlistAddedBy): string {
  if (addedBy.type === 'self') {
    return 'You';
  }
  return `${addedBy.name} (${addedBy.role})`;
}

type WatchlistGridContext = {
  isActionInFlight: boolean;
  watchlistFetching: boolean;
  onToggleActive: (subscription: WatchlistSubscriptionView, nextActive: boolean) => void;
  onUnsubscribeClick: (subscription: WatchlistSubscriptionView) => void;
  isUnsubscribeBusy: (id: string) => boolean;
};

const WATCHLIST_COLUMN_DEFS: ColDef<WatchlistSubscriptionView>[] = [
  {
    headerName: 'Notification',
    field: 'name',
    flex: 2,
    minWidth: 220,
    cellRenderer: (params: ICellRendererParams<WatchlistSubscriptionView>) => {
      const subscription = params.data;
      if (!subscription) {
        return null;
      }
      return (
        <div className="min-w-[220px]">
          <span className="text-sm font-semibold text-foreground">{subscription.name}</span>
          <p className="mt-1 text-sm text-default-500">{subscription.description}</p>
        </div>
      );
    },
  },
  {
    headerName: 'Delivery to you',
    field: 'frequency',
    flex: 1,
    minWidth: 130,
    valueFormatter: (params) => formatWatchlistFrequency(params.value),
  },
  {
    headerName: 'Delivery time',
    field: 'deliveryTimeLabel',
    flex: 1,
    minWidth: 130,
  },
  {
    headerName: 'Added by',
    colId: 'addedBy',
    flex: 1,
    minWidth: 130,
    valueGetter: (params) => (params.data ? formatAddedBy(params.data.addedBy) : ''),
  },
  {
    headerName: 'Active',
    colId: 'active',
    width: 90,
    cellRenderer: (params: ICellRendererParams<WatchlistSubscriptionView>) => {
      const subscription = params.data;
      const ctx = params.context as WatchlistGridContext | undefined;
      if (!subscription || !ctx) {
        return null;
      }
      return (
        <Switch
          size="sm"
          color="success"
          isSelected={subscription.isActive}
          isDisabled={ctx.isActionInFlight || ctx.watchlistFetching}
          aria-label={`Active for ${subscription.name}`}
          onValueChange={(next) => {
            ctx.onToggleActive(subscription, next);
          }}
        />
      );
    },
  },
  {
    headerName: 'Actions',
    colId: 'actions',
    width: 130,
    headerClass: 'text-right !px-4',
    cellClass: '!px-4 text-right',
    cellRenderer: (params: ICellRendererParams<WatchlistSubscriptionView>) => {
      const subscription = params.data;
      const ctx = params.context as WatchlistGridContext | undefined;
      if (!subscription || !ctx) {
        return null;
      }
      return (
        <button
          type="button"
          className="text-sm font-medium text-danger hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={ctx.isActionInFlight || ctx.watchlistFetching}
          onClick={() => ctx.onUnsubscribeClick(subscription)}
        >
          {ctx.isUnsubscribeBusy(subscription.id) ? 'Unsubscribing…' : 'Unsubscribe'}
        </button>
      );
    },
  },
];

type PendingAction =
  | { type: 'toggle'; id: string }
  | { type: 'pause-all' }
  | { type: 'unsubscribe'; id: string };

type WatchlistTableBodyProps = {
  isLoading: boolean;
  isListEmpty: boolean;
  filteredSubscriptions: WatchlistSubscriptionView[];
  gridContext: WatchlistGridContext;
};

function getWatchlistRowClassName(params: { data?: WatchlistSubscriptionView }): string {
  if (params.data?.isActive === false) {
    return 'opacity-75';
  }
  return '';
}

function renderWatchlistTableBody({
  isLoading,
  isListEmpty,
  filteredSubscriptions,
  gridContext,
}: WatchlistTableBodyProps) {
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <CtySpinner size="lg" />
      </div>
    );
  }

  if (isListEmpty) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
        <Icon
          icon="solar:bell-off-linear"
          className="text-default-300 mb-4"
          width={48}
          height={48}
        />
        <p className="text-base font-medium text-foreground">No notifications yet</p>
        <p className="mt-2 text-sm text-default-500 max-w-md">
          When an admin adds you to email notifications — or you subscribe yourself — they will
          appear here so you can pause or unsubscribe.
        </p>
      </div>
    );
  }

  if (filteredSubscriptions.length === 0) {
    return (
      <div className="px-4 py-16 text-center text-default-400">
        No notifications match your search.
      </div>
    );
  }

  return (
    <HorizontalRulesAgGrid<WatchlistSubscriptionView>
      rowData={filteredSubscriptions}
      columnDefs={WATCHLIST_COLUMN_DEFS}
      getRowId={(params) => params.data.id}
      gridPersistenceKey="watchlist-settings-grid"
      getRowClass={getWatchlistRowClassName}
      context={gridContext}
    />
  );
}

function WatchlistSettings() {
  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const currentUserId = user?.sub;

  const {
    data: watchlistData,
    isLoading: watchlistLoading,
    isFetching: watchlistFetching,
    isError: watchlistError,
  } = useGetWatchlistQuery();
  const [updateWatchlistSubscription] = useUpdateWatchlistSubscriptionMutation();

  const [searchQuery, setSearchQuery] = useState('');
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [unsubscribeTarget, setUnsubscribeTarget] = useState<WatchlistSubscriptionView | null>(null);
  const [optimisticActiveById, setOptimisticActiveById] = useState<Record<string, boolean>>({});

  const subscriptions = useMemo(() => {
    const records = watchlistData?.data ?? [];
    const views = mapWatchlistRecordsToViews(records, currentUserId);
    return views.map((view) => ({
      ...view,
      isActive: optimisticActiveById[view.id] ?? view.isActive,
    }));
  }, [watchlistData, currentUserId, optimisticActiveById]);

  const filteredSubscriptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return subscriptions;
    }
    return subscriptions.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query)
    );
  }, [subscriptions, searchQuery]);

  const allPaused =
    subscriptions.length > 0 && subscriptions.every((item) => !item.isActive);
  const isListEmpty = subscriptions.length === 0;
  const isActionInFlight = pendingAction !== null;

  const clearOptimisticState = useCallback(() => {
    setOptimisticActiveById({});
  }, []);

  const handleNotFound = useCallback(() => {
    CtyShowToast(checkType.error, 'This notification is no longer available.');
    clearOptimisticState();
  }, [clearOptimisticState]);

  const handleToggleActive = useCallback(
    async (subscription: WatchlistSubscriptionView, nextActive: boolean) => {
      if (isActionInFlight) {
        return;
      }

      setOptimisticActiveById((current) => ({
        ...current,
        [subscription.id]: nextActive,
      }));
      setPendingAction({ type: 'toggle', id: subscription.id });

      try {
        await updateWatchlistSubscription({
          config_id: subscription.id,
          state: nextActive ? 'active' : 'paused',
        }).unwrap();
        clearOptimisticState();
        CtyShowToast(
          checkType.success,
          nextActive ? 'Notification resumed.' : 'Notification paused.'
        );
      } catch (error) {
        setOptimisticActiveById((current) => {
          const next = { ...current };
          delete next[subscription.id];
          return next;
        });
        if (isWatchlistNotFoundError(error)) {
          handleNotFound();
          return;
        }
        CtyShowToast(checkType.error, 'Failed to update notification.');
      } finally {
        setPendingAction(null);
      }
    },
    [clearOptimisticState, handleNotFound, isActionInFlight, updateWatchlistSubscription]
  );

  const handlePauseAll = useCallback(
    async (nextAllActive: boolean) => {
      if (isActionInFlight || isListEmpty) {
        return;
      }

      const nextState = nextAllActive ? 'active' : 'paused';
      const previousOptimistic = optimisticActiveById;
      setOptimisticActiveById(
        Object.fromEntries(subscriptions.map((item) => [item.id, nextAllActive]))
      );
      setPendingAction({ type: 'pause-all' });

      try {
        await Promise.all(
          subscriptions.map((subscription) =>
            updateWatchlistSubscription({
              config_id: subscription.id,
              state: nextState,
            }).unwrap()
          )
        );
        clearOptimisticState();
        CtyShowToast(
          checkType.success,
          nextAllActive ? 'All notifications resumed.' : 'All notifications paused.'
        );
      } catch (error) {
        setOptimisticActiveById(previousOptimistic);
        if (isWatchlistNotFoundError(error)) {
          handleNotFound();
          return;
        }
        CtyShowToast(checkType.error, 'Failed to update notifications.');
      } finally {
        setPendingAction(null);
      }
    },
    [
      clearOptimisticState,
      handleNotFound,
      isActionInFlight,
      isListEmpty,
      optimisticActiveById,
      subscriptions,
      updateWatchlistSubscription,
    ]
  );

  const handleConfirmUnsubscribe = useCallback(async () => {
    if (!unsubscribeTarget || isActionInFlight) {
      return;
    }

    const target = unsubscribeTarget;
    setPendingAction({ type: 'unsubscribe', id: target.id });

    try {
      await updateWatchlistSubscription({
        config_id: target.id,
        state: 'unsubscribed',
      }).unwrap();
      setUnsubscribeTarget(null);
      CtyShowToast(checkType.success, 'You have been unsubscribed.');
    } catch (error) {
      if (isWatchlistNotFoundError(error)) {
        setUnsubscribeTarget(null);
        handleNotFound();
        return;
      }
      CtyShowToast(checkType.error, 'Failed to unsubscribe.');
    } finally {
      setPendingAction(null);
    }
  }, [handleNotFound, isActionInFlight, unsubscribeTarget, updateWatchlistSubscription]);

  const isUnsubscribeBusy = (id: string) =>
    pendingAction?.type === 'unsubscribe' && pendingAction.id === id;

  const watchlistGridContext = useMemo<WatchlistGridContext>(
    () => ({
      isActionInFlight,
      watchlistFetching,
      onToggleActive: (subscription, nextActive) => {
        handleToggleActive(subscription, nextActive).catch(() => undefined);
      },
      onUnsubscribeClick: setUnsubscribeTarget,
      isUnsubscribeBusy,
    }),
    [handleToggleActive, isActionInFlight, isUnsubscribeBusy, watchlistFetching]
  );

  return (
    <div className="flex flex-col gap-5 h-full min-h-0">
      <PageHeaderWithErrorBoundary
        heading="Watchlist"
        description="Track and manage everything you follow — agents, tickets, and activity you care about — in one place."
        pageHeaderClass="!px-0 !gap-1 !pb-0"
      />

      <EmailSubscriptionBanner />

      {watchlistError && (
        <div className="rounded-lg border border-danger bg-danger/10 px-4 py-3">
          <span className="text-sm text-danger">Failed to load your watchlist.</span>
        </div>
      )}

      <div className="rounded-xl border border-default-200 bg-content1 px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground">Your watchlist</h2>
            <p className="mt-1 text-sm text-default-500 leading-relaxed max-w-3xl">
              Everything that emails <span className="font-semibold text-foreground">you</span>, in
              one place. Pausing or unsubscribing here affects only you — never your teammates.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 shrink-0">
            <Input
              classNames={{
                base: 'w-full sm:w-[240px]',
                inputWrapper: 'h-10 rounded-full border-default-300 bg-content1 shadow-none',
              }}
              size="sm"
              variant="bordered"
              placeholder="Search your watchlist"
              value={searchQuery}
              onValueChange={setSearchQuery}
              startContent={
                <Icon icon="solar:magnifer-linear" className="text-default-400 shrink-0" width={18} />
              }
              isClearable
              onClear={() => setSearchQuery('')}
              aria-label="Search your watchlist"
            />
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm font-medium text-default-600 whitespace-nowrap">
                {allPaused ? 'Resume all' : 'Pause all'}
              </span>
              <Switch
                size="sm"
                color="success"
                isSelected={!allPaused}
                isDisabled={isListEmpty || isActionInFlight || watchlistFetching}
                aria-label={allPaused ? 'Resume all' : 'Pause all'}
                onValueChange={(next) => {
                  handlePauseAll(next).catch(() => undefined);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col rounded-xl border border-default-200 bg-content1 overflow-hidden">
        <div className="px-4 sm:px-5 py-4 border-b border-default-200">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-foreground">
              Notification rules you&apos;re on
            </h2>
            <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
              {subscriptions.length}
            </span>
          </div>
          <p className="mt-1 text-sm text-default-500">
            Email rules for matching tickets. Unsubscribing only affects you.
          </p>
        </div>

        {renderWatchlistTableBody({
          isLoading: watchlistLoading,
          isListEmpty,
          filteredSubscriptions,
          gridContext: watchlistGridContext,
        })}
      </div>

      <SimpleDeleteConfirmationModal
        isOpen={unsubscribeTarget !== null}
        onOpenChange={(open) => {
          if (!open && !isActionInFlight) {
            setUnsubscribeTarget(null);
          }
        }}
        title="Unsubscribe from notification?"
        body={
          unsubscribeTarget ? (
            <p className="text-sm text-default-600">
              You will stop receiving emails for{' '}
              <span className="font-semibold text-foreground">{unsubscribeTarget.name}</span>. This
              affects only you — other recipients are unchanged. Only an admin can add you back to
              this notification.
            </p>
          ) : null
        }
        requireTypedConfirmation={false}
        confirmLabel="Unsubscribe"
        cancelLabel="Cancel"
        confirmColor="danger"
        isConfirmLoading={pendingAction?.type === 'unsubscribe'}
        onConfirm={handleConfirmUnsubscribe}
      />
    </div>
  );
}

export default WatchlistSettings;
